import React, { useContext, useState } from 'react';
import {
  PaymentMethodMessagingElement,
  useStripe,
} from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import paymentAPI from '../../../deprecated/payment/api';
import { IAppState } from '../../../../store';
import { Coupon } from 'src/pages/deprecated/payment/types';
import { TagData } from 'src/store/signup-workflow/types';
import styles from './afterpay-component.module.scss';
import { ToastContext } from 'src/components/toast/toast';
import {
  CUSTOMER_BILLING_ADDRESS,
  CUSTOMER_SHIPPING_ADDRESS,
} from 'src/pages/constants';
import ProductService from 'src/services/product-service';
import { EVENTS, trackSignupFunnel } from 'src/util/track';
import { useAppSelector } from 'src/store/hooks';
import { checkQuizStatus } from 'src/services/quiz-service';
import {
  getNutritionCoachingPriceId,
  getNutritionCoachingPricing,
} from 'src/pages/subscription-new/utils/plans.util';
import { shouldShowChallengeNutritionUpsell } from 'src/pages/payment-new/utils/signupPurchase.util';
import { isUserFromAustralia } from 'src/lib/utils';

interface ChallengeBundleAfterPayProps {
  priceId: string;
  finalPrice: number;
  coupon: Coupon | undefined;
  isChallengeAccepted: boolean;
  tagId: number;
  challengeObject: TagData | null;
  futureCommencement: boolean;
  productId: string;
  stripeCustomer: any;
  isDisabled?: boolean;
}

const ChallengeBundleAfterPayComponent = ({
  priceId,
  finalPrice,
  coupon,
  isChallengeAccepted,
  tagId,
  challengeObject,
  futureCommencement,
  productId,
  stripeCustomer,
  isDisabled = false,
}: ChallengeBundleAfterPayProps) => {
  const user = useSelector((state: IAppState) => state.auth?.user);
  const { addToast } = useContext(ToastContext);
  const { showFreeTrial, allPlansSorted } = useAppSelector(
    state => state.signUp
  );
  const stripe = useStripe();

  const nutritionCoachingPriceId = getNutritionCoachingPriceId(allPlansSorted);
  const nutritionCoachingPrice = Number(
    getNutritionCoachingPricing(allPlansSorted)
  );
  const showCoachingUpsell = shouldShowChallengeNutritionUpsell({
    isChallengePackageFlow: true,
    isAustraliaUser: isUserFromAustralia(),
    nutritionCoachingPriceId,
    nutritionCoachingPrice,
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleAfterPayClick = async () => {
    if (!stripe || !user) return;

    trackSignupFunnel(EVENTS.signupPaymentSubmitted, {
      payment_method: 'afterpay',
      selected_plan_id: priceId,
      total_price: finalPrice,
    });

    setIsLoading(true);

    try {
      let quizFinalStatus = false;
      const quizStatusRes = await checkQuizStatus({ email: user.email });
      if (quizStatusRes.status === 200 && quizStatusRes?.data?.data) {
        quizFinalStatus = true;
      }

      const invoicePayload: Record<string, any> = {
        customerID: stripeCustomer.id,
        productId,
        priceIDs: [priceId],
        promoID: coupon?.id || null,
        userID: user.id,
        isChallenge: !!isChallengeAccepted,
        isCardPayment: false,
        isAfterPay: true,
        futureCommencement,
        isQuiz: quizFinalStatus,
        isFreeTrial: showFreeTrial,
        isChallengePackage: true,
      };

      if (isChallengeAccepted && challengeObject?.id) {
        invoicePayload.challenge_ids = [challengeObject.id];
      }

      const purchaseResponse = await paymentAPI.post(
        'stripe/invoice/purchase',
        invoicePayload
      );

      if (!purchaseResponse.data.isSuccess) {
        throw new Error(purchaseResponse.data.message || 'Purchase failed');
      }

      if (!purchaseResponse.data.requiresPaymentCompletion) {
        addToast('Purchase completed!', 'success', 5000);
        setIsLoading(false);
        return;
      }

      const { clientSecret } = purchaseResponse.data;

      localStorage.setItem(
        'payObj',
        JSON.stringify({
          type: 'challengeBundle',
          userId: user.id,
          priceId,
          coupon,
          totalPrice: finalPrice.toString(),
          futureCommencement,
          tagId,
          showCoachingUpsell,
          nutritionCoachingPriceId,
          nutritionCoachingPrice,
        })
      );

      const result = await stripe.confirmAfterpayClearpayPayment(clientSecret, {
        return_url: `${window.location.origin}/confirmAfterPay`,
        mandate_data: await ProductService.getCustomerAcceptance(),
        payment_method: CUSTOMER_BILLING_ADDRESS,
        shipping: CUSTOMER_SHIPPING_ADDRESS,
      });

      if (result.error) {
        trackSignupFunnel(EVENTS.signupPaymentFailed, {
          payment_method: 'afterpay',
          failure_stage: 'payment_authorization',
          error_message: result.error.message || 'Afterpay payment failed',
          selected_plan_id: priceId,
        });
        addToast('Something went wrong. Please try again.', 'danger', 5000);
        localStorage.setItem('payObj', '{}');
        setIsLoading(false);
      }
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Payment failed. Please try again.';
      trackSignupFunnel(EVENTS.signupPaymentFailed, {
        payment_method: 'afterpay',
        failure_stage: 'purchase_request',
        error_message: message,
        selected_plan_id: priceId,
      });
      addToast(message, 'danger', 5000);
      localStorage.setItem('payObj', '{}');
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.mainDiv}>
        {stripe && (
          <div style={{ margin: '10px' }}>
            <PaymentMethodMessagingElement
              options={{
                amount: parseInt(finalPrice.toFixed(2)) * 100,
                currency: 'AUD',
                paymentMethodTypes: ['afterpay_clearpay'],
                countryCode: 'AU',
              }}
            />
          </div>
        )}
      </div>
      <Button
        onClick={handleAfterPayClick}
        style={{ marginBottom: '10px' }}
        disabled={isLoading || !stripeCustomer?.id || isDisabled}>
        {isLoading ? 'Processing' : 'Finalise with Afterpay'}
      </Button>
    </div>
  );
};

export default ChallengeBundleAfterPayComponent;
